import {
  Badge,
  Box,
  Button,
  Card,
  Container,
  Group,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useEffect, useMemo, useState } from "react";
import {
  DEFAULT_FURNITURE_CATALOG_CATEGORY_KEY,
  DRAFT_LIBRARY_CATEGORY_KEY,
  FURNITURE_CATALOG_CATEGORIES,
  listCollectionsForCategory,
  resolveCatalogCategoryName,
  resolveCatalogCollectionName,
} from "../../shared/furniture-catalog";
import { resolvePaletteColors } from "../../shared/furniture-palette";
import type { FurnitureDefinition } from "../../shared/types";
import { FurniturePreviewCanvas } from "../components/FurniturePreviewCanvas";
import type { FurnitureInventoryEntry } from "../lib/api";

interface Props {
  catalog: FurnitureDefinition[];
  inventory: FurnitureInventoryEntry[];
  loading: boolean;
  onRefresh: () => Promise<void>;
  onBuyFurniture: (furnitureId: string, colorVariantId?: string) => Promise<void>;
  onRemoveFurniture?: (furnitureId: string) => Promise<void>;
  isAdmin?: boolean;
  currentAddress?: string;
  onEditFurniture?: (item: FurnitureDefinition) => void;
}

function formatInteraction(item: FurnitureDefinition): string {
  return item.interactionType ?? (item.sittable ? "sit" : "none");
}

function resolveItemCategory(item: FurnitureDefinition): string {
  return item.catalogCategory ?? DEFAULT_FURNITURE_CATALOG_CATEGORY_KEY;
}

function resolveItemCollection(item: FurnitureDefinition): string {
  return item.catalogCollection ?? "";
}

export function FurnitureCatalogScreen({
  catalog,
  inventory,
  loading,
  onRefresh,
  onBuyFurniture,
  onRemoveFurniture,
  isAdmin = false,
  currentAddress,
  onEditFurniture,
}: Props) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [selectedCategoryKey, setSelectedCategoryKey] = useState<string>("");
  const [selectedCollectionKey, setSelectedCollectionKey] = useState<string>("");
  const [buyingId, setBuyingId] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [selectedVariantByFurnitureId, setSelectedVariantByFurnitureId] =
    useState<Record<string, string>>({});

  const normalizedCurrentAddress = (currentAddress ?? "").trim().toLowerCase();

  const inventoryById = useMemo(() => {
    const totals = new Map<string, number>();
    for (const entry of inventory) {
      const prev = totals.get(entry.furnitureId) ?? 0;
      totals.set(entry.furnitureId, prev + Math.max(0, entry.quantity));
    }
    return totals;
  }, [inventory]);

  const inventoryByVariantKey = useMemo(() => {
    const quantities = new Map<string, number>();
    for (const entry of inventory) {
      const key = `${entry.furnitureId}:${entry.colorVariantId ?? ""}`;
      const prev = quantities.get(key) ?? 0;
      quantities.set(key, prev + Math.max(0, entry.quantity));
    }
    return quantities;
  }, [inventory]);

  const categoryOptions = useMemo(() => {
    const keys = new Set<string>();
    for (const item of catalog) {
      keys.add(resolveItemCategory(item));
    }
    if (normalizedCurrentAddress) {
      keys.add(DRAFT_LIBRARY_CATEGORY_KEY);
    }
    const known = FURNITURE_CATALOG_CATEGORIES.filter((entry) =>
      keys.has(entry.key)
    ).map((entry) => ({ value: entry.key, label: entry.name }));
    const unknown = Array.from(keys)
      .filter(
        (key) => !FURNITURE_CATALOG_CATEGORIES.some((entry) => entry.key === key)
      )
      .sort((left, right) => left.localeCompare(right))
      .map((key) => ({
        value: key,
        label: resolveCatalogCategoryName(key),
      }));
    return [...known, ...unknown];
  }, [catalog, normalizedCurrentAddress]);

  useEffect(() => {
    if (categoryOptions.length === 0) {
      setSelectedCategoryKey("");
      return;
    }
    if (categoryOptions.some((entry) => entry.value === selectedCategoryKey)) {
      return;
    }
    setSelectedCategoryKey(categoryOptions[0].value);
  }, [categoryOptions, selectedCategoryKey]);

  const collectionOptions = useMemo(() => {
    if (!selectedCategoryKey) {
      return [];
    }
    const validCollections = new Set<string>();
    for (const item of catalog) {
      if (resolveItemCategory(item) !== selectedCategoryKey) {
        continue;
      }
      const key = resolveItemCollection(item);
      if (key) {
        validCollections.add(key);
      }
    }
    const known = listCollectionsForCategory(selectedCategoryKey).map(
      (entry) => ({ value: entry.key, label: entry.name })
    );
    const unknown = Array.from(validCollections)
      .filter((key) => !known.some((entry) => entry.value === key))
      .sort((left, right) => left.localeCompare(right))
      .map((key) => ({
        value: key,
        label: resolveCatalogCollectionName(key),
      }));
    return [...known, ...unknown];
  }, [catalog, selectedCategoryKey]);

  useEffect(() => {
    if (collectionOptions.length === 0) {
      setSelectedCollectionKey("");
      return;
    }
    if (
      collectionOptions.some((entry) => entry.value === selectedCollectionKey)
    ) {
      return;
    }
    setSelectedCollectionKey(collectionOptions[0].value);
  }, [collectionOptions, selectedCollectionKey]);

  const filteredCatalog = useMemo(() => {
    const q = search.trim().toLowerCase();
    return catalog.filter((item) => {
      if (
        selectedCategoryKey &&
        resolveItemCategory(item) !== selectedCategoryKey
      ) {
        return false;
      }
      if (
        selectedCollectionKey &&
        resolveItemCollection(item) !== selectedCollectionKey
      ) {
        return false;
      }
      if (!q) {
        return true;
      }
      const hay =
        `${item.name} ${item.description ?? ""} ${item.artist ?? ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [catalog, search, selectedCategoryKey, selectedCollectionKey]);

  const selected =
    filteredCatalog.find((item) => item.id === selectedId) ??
    filteredCatalog[0] ??
    null;

  const resolveDefaultVariantId = (
    item: FurnitureDefinition
  ): string | undefined => {
    const variants = item.colorVariants ?? [];
    if (variants.length === 0) {
      return undefined;
    }
    return (
      variants.find((variant) => variant.id === item.defaultColorVariantId)
        ?.id ?? variants[0]?.id
    );
  };

  const resolveVariantId = (item: FurnitureDefinition): string | undefined => {
    const variants = (item.colorVariants ?? []).filter(
      (variant) => variant.active !== false
    );
    if (variants.length === 0) {
      return undefined;
    }
    const selectedVariant = selectedVariantByFurnitureId[item.id];
    return (
      variants.find((variant) => variant.id === selectedVariant)?.id ??
      variants.find((variant) => variant.id === item.defaultColorVariantId)?.id ??
      variants[0]?.id
    );
  };

  const selectedVariantId = selected ? resolveVariantId(selected) : undefined;
  const selectedPreviewVariantId = selectedVariantId;
  const selectedVariant = selectedVariantId
    ? (selected?.colorVariants ?? []).find(
        (variant) => variant.id === selectedVariantId
      )
    : undefined;
  const listedVariants = (selected?.colorVariants ?? []).filter(
    (variant) => variant.active !== false
  );
  const selectedIsDraft = selected?.itemState === "draft";
  const canBuySelected =
    selected != null &&
    !selectedIsDraft &&
    ((selected.colorVariants?.length ?? 0) === 0 || listedVariants.length > 0);
  const selectedUnitPrice =
    selectedVariant?.priceCoins ?? selected?.priceCoins ?? 0;
  const selectedIsOwnDraft =
    selected?.itemState === "draft" &&
    (selected.sandboxOwnerAddress?.trim().toLowerCase() ===
      normalizedCurrentAddress ||
      selected.createdBy?.trim().toLowerCase() === normalizedCurrentAddress);
  const canEditSelected = Boolean(
    selected && onEditFurniture && (isAdmin || selectedIsOwnDraft)
  );

  const handleBuy = async (item: FurnitureDefinition) => {
    setBuyingId(item.id);
    try {
      await onBuyFurniture(item.id, resolveVariantId(item));
      notifications.show({
        title: "Purchased",
        message: `${item.name} added to your inventory.`,
        color: "teal",
      });
      await onRefresh();
    } catch (error) {
      notifications.show({
        title: "Purchase failed",
        message: error instanceof Error ? error.message : "Please retry.",
        color: "red",
      });
    } finally {
      setBuyingId(null);
    }
  };

  const handleRemove = async (item: FurnitureDefinition) => {
    if (!onRemoveFurniture) return;
    setRemovingId(item.id);
    try {
      await onRemoveFurniture(item.id);
      notifications.show({
        title: "Removed from store",
        message: `${item.name} is no longer listed.`,
        color: "teal",
      });
      await onRefresh();
    } catch (error) {
      notifications.show({
        title: "Remove failed",
        message: error instanceof Error ? error.message : "Please retry.",
        color: "red",
      });
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <Container size="xl" py="md">
      <Stack gap="md">
        <Group justify="space-between" align="end">
          <Stack gap={2}>
            <Title order={2} c="dark">
              Furniture Catalog
            </Title>
            <Text size="sm" c="gray.7">
              Browse catalog structure by category and collection. Drafts in your
              sandbox are visible but not purchasable.
            </Text>
          </Stack>
          <TextInput
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
            placeholder="Search items"
            w={280}
          />
        </Group>

        <Group grow>
          <Select
            label="Category"
            value={selectedCategoryKey || null}
            data={categoryOptions}
            onChange={(value) => setSelectedCategoryKey(value ?? "")}
          />
          <Select
            label="Collection"
            value={selectedCollectionKey || null}
            data={collectionOptions}
            onChange={(value) => setSelectedCollectionKey(value ?? "")}
            disabled={collectionOptions.length === 0}
          />
        </Group>

        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
          <Card
            p="sm"
            radius="md"
            style={{ border: "1px solid rgba(0,0,0,0.08)", minHeight: 560 }}
          >
            <Stack gap="sm">
              <Group justify="space-between">
                <Text fw={600} c="gray.8">
                  Items
                </Text>
                <Badge variant="light" color={loading ? "yellow" : "gray"}>
                  {loading ? "Loading" : `${filteredCatalog.length} items`}
                </Badge>
              </Group>
              <Box style={{ maxHeight: 500, overflowY: "auto", paddingRight: 4 }}>
                <Stack gap="xs">
                  {filteredCatalog.map((item) => {
                    const selectedRow = selected?.id === item.id;
                    const defaultVariantId = resolveDefaultVariantId(item);
                    const listPrice =
                      (item.colorVariants ?? []).find(
                        (variant) => variant.id === resolveVariantId(item)
                      )?.priceCoins ?? item.priceCoins;
                    return (
                      <Card
                        key={item.id}
                        p="xs"
                        radius="md"
                        style={{
                          border: selectedRow
                            ? "1px solid rgba(253,126,20,0.5)"
                            : "1px solid rgba(0,0,0,0.08)",
                          background: selectedRow ? "rgba(253,126,20,0.06)" : "#fff",
                          cursor: "pointer",
                        }}
                        onClick={() => setSelectedId(item.id)}
                      >
                        <Group align="flex-start" wrap="nowrap">
                          <Box
                            style={{
                              width: 90,
                              height: 72,
                              borderRadius: 8,
                              overflow: "hidden",
                              border: "1px solid rgba(0,0,0,0.08)",
                              background:
                                "linear-gradient(180deg,#eef2f7 0%,#f8f9fb 100%)",
                              flexShrink: 0,
                            }}
                          >
                            <FurniturePreviewCanvas
                              modelUrl={item.modelUrl}
                              tintColor={item.tintColor}
                              colorVariants={item.colorVariants}
                              defaultColorVariantId={item.defaultColorVariantId}
                              colorVariantId={defaultVariantId}
                              paletteColors={resolvePaletteColors(
                                item.colorVariants,
                                defaultVariantId,
                                item.defaultColorVariantId
                              )}
                            />
                          </Box>
                          <Stack gap={2} style={{ minWidth: 0 }}>
                            <Group justify="space-between" wrap="nowrap">
                              <Text fw={600} size="sm" truncate>
                                {item.name}
                              </Text>
                              <Badge size="xs" color="yellow" variant="light">
                                {listPrice}
                              </Badge>
                            </Group>
                            <Text size="xs" c="dimmed" lineClamp={2}>
                              {item.description ?? "No description"}
                            </Text>
                            <Group gap={4}>
                              <Badge size="xs" variant="outline">
                                {item.blockWidth}x{item.blockDepth}
                              </Badge>
                              <Badge size="xs" variant="outline" color="blue">
                                {formatInteraction(item)}
                              </Badge>
                              {item.itemState === "draft" ? (
                                <Badge size="xs" color="orange" variant="light">
                                  Draft
                                </Badge>
                              ) : null}
                            </Group>
                          </Stack>
                        </Group>
                      </Card>
                    );
                  })}
                  {filteredCatalog.length === 0 ? (
                    <Text size="sm" c="dimmed">
                      {selectedCategoryKey === DRAFT_LIBRARY_CATEGORY_KEY
                        ? "No drafts yet. Open Creator to save your first sandbox item."
                        : "No furniture in this category/collection."}
                    </Text>
                  ) : null}
                </Stack>
              </Box>
            </Stack>
          </Card>

          <Card
            p="sm"
            radius="md"
            style={{ border: "1px solid rgba(0,0,0,0.08)", minHeight: 560 }}
          >
            {selected ? (
              <Stack gap="sm" h="100%">
                <Group justify="space-between" align="start">
                  <Stack gap={2}>
                    <Text fw={700} c="dark">
                      {selected.name}
                    </Text>
                    <Text size="xs" c="dimmed">
                      ID: {selected.id}
                    </Text>
                  </Stack>
                  <Badge color="yellow" variant="light">
                    {selectedUnitPrice} coins
                  </Badge>
                </Group>

                <Box style={{ height: 260 }}>
                  <FurniturePreviewCanvas
                    modelUrl={selected.modelUrl}
                    tintColor={selected.tintColor}
                    inRoom
                    interactive
                    blockWidth={selected.blockWidth}
                    blockDepth={selected.blockDepth}
                    sittable={selected.sittable}
                    interactionType={selected.interactionType}
                    walkableTop={selected.walkableTop}
                    stackableTop={selected.stackableTop}
                    stackHeight={selected.stackHeight}
                    teleportGroup={selected.teleportGroup}
                    actionOpenAnimation={selected.actionOpenAnimation}
                    actionCloseAnimation={selected.actionCloseAnimation}
                    seatOffsetX={selected.seatOffsetX}
                    seatOffsetY={selected.seatOffsetY}
                    seatOffsetZ={selected.seatOffsetZ}
                    colorVariants={selected.colorVariants}
                    defaultColorVariantId={selected.defaultColorVariantId}
                    colorVariantId={selectedPreviewVariantId}
                    paletteColors={resolvePaletteColors(
                      selected.colorVariants,
                      selectedPreviewVariantId,
                      selected.defaultColorVariantId
                    )}
                  />
                </Box>

                <Stack gap={4}>
                  <Text size="sm" c="gray.8">
                    {selected.description ?? "No description"}
                  </Text>
                  <Text size="xs" c="dimmed">
                    Artist: {selected.artist ?? "Unknown"}
                  </Text>
                  <Text size="xs" c="dimmed">
                    Uploaded by:{" "}
                    {selected.createdByDisplayName ?? selected.createdBy ?? "Unknown"}
                  </Text>
                  <Text size="xs" c="dimmed">
                    Category:{" "}
                    {resolveCatalogCategoryName(resolveItemCategory(selected))}
                  </Text>
                  <Text size="xs" c="dimmed">
                    Collection:{" "}
                    {resolveCatalogCollectionName(resolveItemCollection(selected))}
                  </Text>
                  <Group gap={4}>
                    <Badge size="xs" variant="outline">
                      Footprint {selected.blockWidth}x{selected.blockDepth}
                    </Badge>
                    <Badge size="xs" variant="outline" color="blue">
                      {formatInteraction(selected)}
                    </Badge>
                    {selected.walkableTop ? (
                      <Badge size="xs" color="teal" variant="light">
                        walkableTop
                      </Badge>
                    ) : null}
                    {selected.stackableTop ? (
                      <Badge size="xs" color="green" variant="light">
                        stackableTop
                      </Badge>
                    ) : null}
                    {typeof selected.stackHeight === "number" ? (
                      <Badge size="xs" variant="outline" color="grape">
                        stackHeight {selected.stackHeight}
                      </Badge>
                    ) : null}
                    {selected.itemState === "draft" ? (
                      <Badge size="xs" variant="light" color="orange">
                        Draft sandbox item
                      </Badge>
                    ) : null}
                  </Group>
                </Stack>

                <Group mt="auto" justify="space-between" align="center">
                  <Stack gap={2}>
                    <Text size="sm" c="gray.7">
                      Owned: {inventoryById.get(selected.id) ?? 0}
                    </Text>
                    {selectedVariantId ? (
                      <Text size="xs" c="dimmed">
                        This color:{" "}
                        {inventoryByVariantKey.get(
                          `${selected.id}:${selectedVariantId}`
                        ) ?? 0}
                      </Text>
                    ) : null}
                  </Stack>
                  <Group gap="xs">
                    {(selected.colorVariants?.length ?? 0) > 0 ? (
                      <Select
                        w={180}
                        size="xs"
                        value={selectedVariantId ?? null}
                        data={listedVariants.map((variant) => ({
                          value: variant.id,
                          label:
                            typeof variant.priceCoins === "number"
                              ? `${variant.name} (${variant.priceCoins})`
                              : variant.name,
                        }))}
                        disabled={listedVariants.length === 0}
                        onChange={(value) => {
                          if (!value) return;
                          setSelectedVariantByFurnitureId((prev) => ({
                            ...prev,
                            [selected.id]: value,
                          }));
                        }}
                      />
                    ) : null}
                    {canEditSelected ? (
                      <Button
                        variant="default"
                        onClick={() => onEditFurniture?.(selected)}
                      >
                        Edit in Creator
                      </Button>
                    ) : null}
                    {isAdmin &&
                    onRemoveFurniture &&
                    selected.itemState === "published" ? (
                      <Button
                        color="red"
                        variant="light"
                        loading={removingId === selected.id}
                        onClick={() => void handleRemove(selected)}
                      >
                        Remove from Store
                      </Button>
                    ) : null}
                    <Button
                      loading={buyingId === selected.id}
                      disabled={!canBuySelected}
                      onClick={() => void handleBuy(selected)}
                    >
                      {selectedIsDraft ? "Not for Sale" : "Buy"}
                    </Button>
                  </Group>
                </Group>
              </Stack>
            ) : (
              <Group justify="center" align="center" h="100%">
                <Text c="dimmed">Select a furniture item.</Text>
              </Group>
            )}
          </Card>
        </SimpleGrid>
      </Stack>
    </Container>
  );
}
